import { useContext } from 'react'
import { Preloader } from 'src/components/Preloader/Preloader'
import Text from 'src/components/ui/Text'
import type { GenerationModel } from 'src/services/apollo/generated/graphql'
import {
  useListLoraRecommendationsByModel,
  useListSimilarLoras,
} from 'src/services/apollo/modelRecommendation'
import { isLora } from 'src/utils/model'
import { track } from 'src/utils/tracker'
import { ModelDetailContext } from '../context'
import ModelItemDisplay, { ModelItemDisplayVariant } from '../ModelItemDisplay'
import { ModelDetailSection } from './ModelDetailSection'

function LoraRecommendationByModelSection(props: {
  modelId: string
  modelVersionId?: string
}) {
  const { data, loading } = useListLoraRecommendationsByModel({
    modelId: props.modelId,
    modelVersionId: props.modelVersionId,
    first: 4,
  })
  if (loading) {
    return <Preloader />
  }
  if (!data || data.length === 0) {
    return null
  }
  const handleLoraClick = (lora: GenerationModel) => {
    track('lora_recommendation_click', {
      scene: 'model-detail',
      loraId: lora.id,
      loraType: lora.type,
      sourceModelId: props.modelId,
      sourceModelVersionId: props.modelVersionId,
    })
  }
  return (
    <ModelDetailSection
      className="mt-4"
      heading={<Text i18nKey="market:model.recommended-loras.title" />}
    >
      <div className="flex flex-col gap-2">
        {data.map((lora, idx) => (
          <ModelItemDisplay
            key={lora.id ?? idx}
            model={lora}
            variant={ModelItemDisplayVariant.ModelDetailLoraRec}
            onClick={handleLoraClick}
          />
        ))}
      </div>
    </ModelDetailSection>
  )
}

function SimilarLorasSection(props: {
  loraId: string
  loraVersionId?: string
}) {
  const { data, loading } = useListSimilarLoras({
    loraId: props.loraId,
    loraVersionId: props.loraVersionId,
    first: 4,
  })
  if (loading) {
    return <Preloader />
  }
  if (!data || data.length === 0) {
    return null
  }
  const handleLoraClick = (lora: GenerationModel) => {
    track('similar_lora_click', {
      scene: 'model-detail',
      loraId: lora.id,
      loraType: lora.type,
      sourceLoraId: props.loraId,
      sourceLoraVersionId: props.loraVersionId,
    })
  }
  return (
    <ModelDetailSection
      className="mt-4"
      heading={<Text i18nKey="market:model.recommended-loras.title" />}
    >
      <div className="flex flex-col gap-2">
        {data.map((lora, idx) => (
          <ModelItemDisplay
            key={lora.id ?? idx}
            model={lora}
            variant={ModelItemDisplayVariant.ModelDetailLoraRec}
            onClick={handleLoraClick}
          />
        ))}
      </div>
    </ModelDetailSection>
  )
}

export default function LoraRecommendationListSection(props: {
  modelId: string
  modelVersionId?: string
}) {
  const { model } = useContext(ModelDetailContext)

  // Render similar loras for LoRAs, and lora recommendations for base models
  const isLoraModel = model?.type ? isLora(model.type) : false
  if (isLoraModel) {
    return (
      <SimilarLorasSection
        loraId={props.modelId}
        loraVersionId={props.modelVersionId}
      />
    )
  } else {
    return (
      <LoraRecommendationByModelSection
        modelId={props.modelId}
        modelVersionId={props.modelVersionId}
      />
    )
  }
}
